import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { Blank } from '../../../../core/models/information.model';
import { InvoiceSiteConfig } from '../../../../core/models/invoice-settings/invoice-settings.model';
import { MapperInvoiceSettingsService } from '../../../../core/mappers/invoice-settings/mapper-invoice-settings.service';
import { ApiInvoiceSettingsService } from '../../../../core/services/api/invoice-settings/api-invoice-settings.service';
import { ApiFormService } from '../../../../core/services/api-form.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-setting-invoice',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './setting-invoice.component.html',
  styleUrl: './setting-invoice.component.css',
})
export class SettingInvoiceComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiInvoiceSettingsService);
  private readonly apiForm = inject(ApiFormService);
  private readonly mapper = inject(MapperInvoiceSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  config = signal<InvoiceSiteConfig | null>(null);
  banks = signal<Blank[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);

  invoiceTypeOptions = [
    { value: 'billing', label: 'Billing' },
    { value: 'tax_invoice', label: 'Tax Invoice' },
  ];

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => this.loadConfig());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadConfig();
  }

  onRefresh(): void {
    this.loadConfig();
  }

  onSave(): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    if (!cfg.invoiceNoPrefix?.trim()) {
      this.toast.error('กรุณาระบุ prefix เลขที่ใบแจ้งหนี้');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }
    const payload = this.mapper.mapSiteConfigToPayload(cfg, userId, menuId);

    this.isSaving.set(true);
    this.api.upsertInvoiceSiteConfig(payload).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.loadConfig();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
        this.isSaving.set(false);
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาด');
        this.isSaving.set(false);
      },
    });
  }

  private loadConfig(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.config.set(null);
      return;
    }

    this.isLoading.set(true);
    this.apiForm.getBank(siteId).subscribe({
      next: (res) => this.banks.set(res ?? []),
      error: () => this.banks.set([]),
    });

    this.api.getInvoiceSiteConfig(siteId).subscribe({
      next: (res) => {
        this.config.set(this.mapper.mapSiteConfig(res as InvoiceSiteConfig & Record<string, unknown>));
        this.isLoading.set(false);
      },
      error: () => {
        this.config.set(null);
        this.isLoading.set(false);
        this.toast.error('โหลดการตั้งค่าใบแจ้งหนี้ล้มเหลว');
      },
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return (this.config()?.id ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
